import { slugifySegment } from "@/lib/product-url";
import { BRANDS_PATH } from "@/lib/route-paths";

interface BrandPathInput {
  brandName?: string | null;
  brandSlug?: string | null;
}

function toNonEmptyString(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function decodeBrandIdentifier(value: string | null | undefined): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;

  try {
    const decoded = decodeURIComponent(normalized).trim();
    return decoded.length > 0 ? decoded : normalized;
  } catch {
    return normalized;
  }
}

export function getCanonicalBrandSlug(input: string | BrandPathInput): string {
  if (typeof input === "string") {
    const normalized = decodeBrandIdentifier(input);
    return normalized ?? slugifySegment(input, "brand");
  }

  const explicitSlug = decodeBrandIdentifier(input.brandSlug);
  if (explicitSlug) {
    return explicitSlug;
  }

  // During the backfill window, preserve the raw brand identifier when no
  // canonical slug is present so legacy names like "A.P.C." still resolve.
  // This intentionally differs from the backend's make_slug fallback.
  const normalizedBrandName = decodeBrandIdentifier(input.brandName);
  return normalizedBrandName ?? slugifySegment(input.brandName, "brand");
}

export function buildCanonicalBrandPath(input: string | BrandPathInput): string {
  return `${BRANDS_PATH}/${encodeURIComponent(getCanonicalBrandSlug(input))}`;
}

export function buildBrandShopPath(input: string | BrandPathInput): string {
  return `${buildCanonicalBrandPath(input)}/shop`;
}

/**
 * Whether the PDP should link the brand name to /brands/{slug}. Brands are
 * canonicalized from product data, but brand pages resolve against the brand
 * index, so the backend reports `brand_page_available`; only an explicit
 * `false` suppresses the link (older payloads without the field keep linking).
 */
export function isBrandPageAvailable(product: Record<string, unknown> | null | undefined): boolean {
  return product?.brand_page_available !== false;
}
